import { InputWrapper, MantineColorsTuple, MantineThemeOverride, MultiSelect, Select, Title, rem } from '@mantine/core';

import { brand, brandPalette } from './branding';

/** The brand's headline/accent colour (DOKA blue, or Umdasch Group's dark blue - see branding.ts).
 * Reserved for the H1 of a view and for links/actions - see the `Title` override below for why it
 * is no longer painted on every heading level. */
const BRAND_PRIMARY = brand.primary;

// InputBase/InputWrapper used to run `unstyled: true` + the UAG blueprint's own
// `classNames.textField.default` here, which put every TextInput/Select (search box, sort/perPage,
// the landing search mask, the date-range facet, ...) on an entirely different design system than
// Button/Card/Badge/SegmentedControl (plain Mantine + App.css's rounded-corner/shadow polish) -
// the "unrund" look the whole catalog UI had (see chat). Dropping the override lets inputs fall
// back to plain Mantine styling, so they pick up the same App.css polish as everything else. The
// only other blueprint page left (ApplicationDetail.tsx) uses no TextInput/Select, so this is
// scoped to our own catalog code in practice.
export const getCustomThemeOverrides = (): MantineThemeOverride => ({
    // The brand's primary as Mantine's primary colour, so filled buttons, light badges, links and
    // the active states follow the brand (Umdasch Group: dark blue) instead of the DOKA theme's.
    colors: { pih: brandPalette() as unknown as MantineColorsTuple },
    primaryColor: 'pih',
    primaryShade: 6,
    /*
     * Heading scale (see design review, point 4). This used to be one global CSS rule in App.css -
     * `.mantine-Title-root { color:#004588; font-weight:800 }` for every `order` - which gave the
     * app exactly two typographic steps: blue-800-large, or dimmed grey. Everything in between
     * (a tab's own heading, an attribute group's caption) had to borrow one of those two, and the
     * intermediate levels ended up either shouting or whispering.
     *
     * Five real steps instead, tightening the letter-spacing only where the type is large enough
     * for it to help. Sizes sit well below Mantine's defaults for h1/h2: this is a dense data
     * application, not an article page, and a headline here leads a pane rather than a page.
     */
    headings: {
        sizes: {
            // 25px, down from 30 (reported: the per-system/article headline is a touch too big).
            // A catalog pane is dense - the object's name has to lead the view without dominating
            // the data underneath it.
            h1: { fontSize: rem(25), lineHeight: '1.2', fontWeight: '700' },
            h2: { fontSize: rem(21), lineHeight: '1.25', fontWeight: '700' },
            h3: { fontSize: rem(19), lineHeight: '1.25', fontWeight: '600' },
            h4: { fontSize: rem(16), lineHeight: '1.3', fontWeight: '600' },
            h5: { fontSize: rem(14), lineHeight: '1.35', fontWeight: '600' },
            h6: { fontSize: rem(13), lineHeight: '1.4', fontWeight: '600' }
        }
    },
    components: {
        /*
         * Only the H1 of a view carries the brand blue; h2 and below inherit the normal text
         * colour. The old global rule painted every heading in it, which put headings and links in
         * the same blue - so the colour stopped signalling "this is clickable", the one job it
         * actually has to do in a catalog full of cross-references (breadcrumbs, usage links,
         * document titles). Blue now means interactive, plus the single top heading of a view.
         *
         * `styles` (rather than `classNames` + a stylesheet rule) because the decision depends on
         * the component's own `order` prop, and because Mantine applies these as inline styles -
         * which is what reliably beats @uag/doka-design's own rules, the same reason SearchMask and
         * LanguageSwitch reach for inline styling.
         */
        Title: Title.extend({
            styles: (_theme, props) => ({
                root: {
                    color: props.order === 1 ? BRAND_PRIMARY : undefined,
                    letterSpacing: props.order !== undefined && props.order <= 2 ? '-0.01em' : undefined
                }
            })
        }),
        InputWrapper: InputWrapper.extend({
            classNames: {
                required: 'text-text-alert -ml-1'
            }
        }),
        // Select/MultiSelect's own <ScrollArea> (default `withScrollArea: true`), used to make a
        // long dropdown option list (language/document-type filters) scrollable past
        // `maxDropdownHeight`, wasn't actually scrollable in practice - see App.css's
        // `.mantine-Combobox-options` rule for the full story and why this pairs with it: with
        // ScrollArea disabled here, that plain CSS rule becomes the ONLY thing constraining the
        // dropdown's height, instead of competing with Mantine's own (here non-functional) one.
        Select: Select.extend({ defaultProps: { withScrollArea: false } }),
        MultiSelect: MultiSelect.extend({ defaultProps: { withScrollArea: false } })
    }
});
